import { FC, useState, useEffect } from 'react';
import reactHtmlParser from 'react-html-parser';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import LegalDisclaimerEditor from 'components/LegalDisclaimerEditor/LegalDisclaimerEditor';
import LinkButton from 'components/buttons/LinkButton/LinkButton';
import { SectionName } from 'modules/home/constants';
import {
  getCurrentRichMedia,
  getDefaultRichMedia,
} from 'modules/benefitsWellness/slices/legalDisclaimerSlice';

import iconEdit from 'images/icon-edit.svg';
import styles from './footerTextSection.module.less';

type FooterTextSectionProps = {
  masterId: string;
  isEditMode: boolean;
};

const FooterTextSection: FC<FooterTextSectionProps> = ({
  masterId,
  isEditMode,
}) => {
  const dispatch = useAppDispatch();

  const {
    content: disclaimerContent,
    defaultContent: defaultDisclaimerContent,
  } = useAppSelector((state) => state.legalDisclaimer);

  const [localEditorContent, setLocalEditorContent] = useState<string>('');
  const [showDisclaimerEditor, setShowDisclaimerEditor] =
    useState<boolean>(false);
  const [isResetEnabled, setIsResetEnabled] = useState<boolean>(false);

  // Fetch current and default content on mount
  useEffect(() => {
    if (masterId) {
      dispatch(getCurrentRichMedia(masterId, SectionName.FOOTER_TEXT));
      dispatch(getDefaultRichMedia(masterId, SectionName.FOOTER_TEXT));
    }
  }, [masterId, dispatch]);

  // Update local editor content when disclaimer content changes
  useEffect(() => {
    setLocalEditorContent(disclaimerContent);
  }, [disclaimerContent]);

  // Enable/disable reset based on content comparison
  useEffect(() => {
    setIsResetEnabled(
      localEditorContent !== defaultDisclaimerContent &&
        !!defaultDisclaimerContent
    );
  }, [localEditorContent, defaultDisclaimerContent]);

  const handleUpdateEditorContent = (data: string) => {
    setLocalEditorContent(data);
  };

  const handleResetToDefault = () => {
    if (defaultDisclaimerContent) {
      setLocalEditorContent(defaultDisclaimerContent);
      dispatch(getCurrentRichMedia(masterId, SectionName.FOOTER_TEXT));
      dispatch(getDefaultRichMedia(masterId, SectionName.FOOTER_TEXT));
    }
  };

  const handleResetToCurrentValue = () => {
    setLocalEditorContent(disclaimerContent);
  };

  return (
    <div className={styles.footerTextWrapper}>
      <EditableHotspot
        alwaysVisible={isEditMode && !disclaimerContent}
        editText="Edit"
        editIcon={<img src={iconEdit} alt="edit" />}
        customModal={(visible: boolean, onClose: Function) => (
          <LegalDisclaimerEditor
            isOpenModal={visible || showDisclaimerEditor}
            closeModal={() => {
              setShowDisclaimerEditor(false);
              onClose();
            }}
            editorContent={localEditorContent}
            benguideId={masterId}
            defaultContent={defaultDisclaimerContent}
            updateEditorContent={handleUpdateEditorContent}
            resetToDefault={handleResetToDefault}
            resetToCurrentValue={handleResetToCurrentValue}
            isResetEnabled={isResetEnabled}
            onCreate={() => {
              dispatch(getCurrentRichMedia(masterId, SectionName.FOOTER_TEXT));
            }}
            onTemplateUpdate={() => {
              dispatch(getDefaultRichMedia(masterId, SectionName.FOOTER_TEXT));
            }}
          />
        )}
      >
        {disclaimerContent ? (
          <div>{reactHtmlParser(disclaimerContent)}</div>
        ) : isEditMode ? (
          <LinkButton onClick={() => setShowDisclaimerEditor(true)}>
            Add Footer
          </LinkButton>
        ) : (
          <></>
        )}
      </EditableHotspot>
    </div>
  );
};

export default FooterTextSection;
